import React, { useState, useRef } from 'react';
import LoadingSpinner from '../LoadingSpinner';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

const PasswordSection = ({
  currentPassword,
  setCurrentPassword,
  newPassword,
  setNewPassword,
  confirmPassword,
  setConfirmPassword,
  passwordMessage,
  isLoading,
  handlePasswordUpdate
}) => {
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const currentPasswordRef = useRef(null);
  const newPasswordRef = useRef(null);
  const confirmPasswordRef = useRef(null);
  return (
    <div className="bg-white rounded-lg p-6">
      <h2 id="password-heading" className="text-xl font-semibold mb-4">Update password</h2>
      
      <form onSubmit={handlePasswordUpdate} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-4 items-center">
          <label htmlFor="currentPassword" className="font-semibold md:text-right md:pr-4">Current password</label>
          <div className="md:col-span-2 relative">
            <input
              ref={currentPasswordRef}
              id="currentPassword"
              type={showCurrentPassword ? "text" : "password"}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              onBlur={() => setShowCurrentPassword(false)}
              className="w-full p-2 pr-10 rounded border-2 border-[#68C7CC]"
              required
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              onClick={() => {
                setShowCurrentPassword(!showCurrentPassword);
                setTimeout(() => {
                  if (currentPasswordRef.current) {
                    currentPasswordRef.current.focus();
                    currentPasswordRef.current.setSelectionRange(currentPassword.length, currentPassword.length);
                  }
                }, 0);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setShowCurrentPassword(!showCurrentPassword);
                  setTimeout(() => {
                    if (currentPasswordRef.current) {
                      currentPasswordRef.current.focus();
                      currentPasswordRef.current.setSelectionRange(currentPassword.length, currentPassword.length);
                    }
                  }, 0);
                }
              }}
              aria-label={showCurrentPassword ? "Hide current password" : "Show current password"}
              aria-pressed={showCurrentPassword}
              tabIndex={0}
            >
              {showCurrentPassword ? (
                <FaEyeSlash className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              ) : (
                <FaEye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              )}
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-4 items-center">
          <label htmlFor="newPassword" className="font-semibold md:text-right md:pr-4">New password</label>
          <div className="md:col-span-2 relative">
            <input
              ref={newPasswordRef}
              id="newPassword"
              type={showNewPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              onBlur={() => setShowNewPassword(false)}
              className="w-full p-2 pr-10 rounded border-2 border-[#68C7CC]"
              required
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              onClick={() => {
                setShowNewPassword(!showNewPassword);
                setTimeout(() => {
                  if (newPasswordRef.current) {
                    newPasswordRef.current.focus();
                    newPasswordRef.current.setSelectionRange(newPassword.length, newPassword.length);
                  }
                }, 0);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setShowNewPassword(!showNewPassword);
                  setTimeout(() => {
                    if (newPasswordRef.current) {
                      newPasswordRef.current.focus();
                      newPasswordRef.current.setSelectionRange(newPassword.length, newPassword.length);
                    }
                  }, 0);
                }
              }}
              aria-label={showNewPassword ? "Hide new password" : "Show new password"}
              aria-pressed={showNewPassword}
              tabIndex={0}
            >
              {showNewPassword ? (
                <FaEyeSlash className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              ) : (
                <FaEye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              )}
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-4 items-center">
          <label htmlFor="confirmPassword" className="font-semibold md:text-right md:pr-4">Confirm password</label>
          <div className="md:col-span-2 relative">
            <input
              ref={confirmPasswordRef}
              id="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onBlur={() => setShowConfirmPassword(false)}
              className="w-full p-2 pr-10 rounded border-2 border-[#68C7CC]"
              required
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              onClick={() => {
                setShowConfirmPassword(!showConfirmPassword);
                setTimeout(() => {
                  if (confirmPasswordRef.current) {
                    confirmPasswordRef.current.focus();
                    confirmPasswordRef.current.setSelectionRange(confirmPassword.length, confirmPassword.length);
                  }
                }, 0);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setShowConfirmPassword(!showConfirmPassword);
                  setTimeout(() => {
                    if (confirmPasswordRef.current) {
                      confirmPasswordRef.current.focus();
                      confirmPasswordRef.current.setSelectionRange(confirmPassword.length, confirmPassword.length);
                    }
                  }, 0);
                }
              }}
              aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
              aria-pressed={showConfirmPassword}
              tabIndex={0}
            >
              {showConfirmPassword ? (
                <FaEyeSlash className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              ) : (
                <FaEye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              )}
            </button>
          </div>
        </div>
        
        {passwordMessage.text && (
          <div className={`col-span-3 text-center ${passwordMessage.type === 'success' ? 'text-green-500' : 'text-red-500'}`}>
            {passwordMessage.text}
          </div>
        )}
        
        <div className="col-span-3 flex justify-center">
          <button
            type="submit"
            className={`px-4 py-2 font-semibold rounded transition-colors duration-200 ${
              currentPassword && newPassword && confirmPassword && !isLoading
                ? 'bg-green-500 text-white hover:bg-green-600'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            disabled={!currentPassword || !newPassword || !confirmPassword || isLoading}
          >
            {isLoading ? (
              <>
                Updating
                <LoadingSpinner className="inline-block ml-2 w-4 h-4" />
              </>
            ) : (
              'Update password'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PasswordSection;
